import { useMemo, useState } from 'react';
import {
  ExampleCallout,
  FieldLabel,
  InfoSection,
  PanelTitle,
  ResultsEmpty,
  ToolPageHeading,
  fmtPct,
  parseNum,
  toolInputClass,
} from './toolShared';

const DEFAULTS = { cf: '', cashIn: '', profit: '', invest: '' };

export function ToolRoiCoc() {
  const [v, setV] = useState(DEFAULTS);

  const coc = useMemo(() => {
    const cf = parseNum(v.cf);
    const cash = parseNum(v.cashIn);
    if (cash <= 0) return null;
    return (cf / cash) * 100;
  }, [v]);

  const roi = useMemo(() => {
    const p = parseNum(v.profit);
    const inv = parseNum(v.invest);
    if (inv <= 0) return null;
    return (p / inv) * 100;
  }, [v]);

  const hasAny = coc !== null || roi !== null;

  return (
    <div className="space-y-8">
      <ToolPageHeading
        title="ROI & cash-on-cash calculator"
        subtitle="Measure cash-on-cash return and simple ROI from your deal inputs."
      />

      <InfoSection title="Understanding ROI vs cash-on-cash return">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <p className="font-display text-xs font-bold uppercase tracking-wide text-[var(--home-miami-blue)]">
              Cash-on-cash return
            </p>
            <p>Annual return on the cash you actually invested, based on pre-tax cash flow.</p>
            <p className="font-mono text-xs text-[var(--home-text-on-dark)]/80">
              (Annual cash flow ÷ Total cash invested) × 100
            </p>
            <ExampleCallout>
              Example: $24,000 cash flow on $200,000 invested = <strong className="text-[var(--home-text-on-dark)]">12%</strong>{' '}
              CoC.
            </ExampleCallout>
          </div>
          <div className="space-y-2">
            <p className="font-display text-xs font-bold uppercase tracking-wide text-[var(--home-miami-blue)]">
              Return on investment (ROI)
            </p>
            <p>Total profit versus total capital invested over your hold (simplified; excludes time value).</p>
            <p className="font-mono text-xs text-[var(--home-text-on-dark)]/80">
              (Total profit ÷ Total investment) × 100
            </p>
            <ExampleCallout>
              Example: $150,000 profit on $200,000 invested = <strong className="text-[var(--home-text-on-dark)]">75%</strong>{' '}
              ROI.
            </ExampleCallout>
          </div>
        </div>
      </InfoSection>

      <div className="grid gap-4 lg:grid-cols-2 lg:items-stretch">
        <div className="space-y-4">
          <div className="overflow-hidden rounded-3xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 p-4 shadow-[0_48px_100px_-50px_rgb(0_0_0/0.85)] ring-1 ring-white/[0.04] backdrop-blur-sm md:p-6">
            <PanelTitle>Cash-on-cash return</PanelTitle>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="rc-cf" tip="Annual pre-tax cash flow after debt service.">
                  Annual cash flow
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="rc-cf"
                    type="text"
                    inputMode="decimal"
                    value={v.cf}
                    onChange={(e) => setV((s) => ({ ...s, cf: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="rc-cash" tip="Total equity checks: down payment, closing, capex, reserves funded." required>
                  Total cash invested
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="rc-cash"
                    type="text"
                    inputMode="decimal"
                    value={v.cashIn}
                    onChange={(e) => setV((s) => ({ ...s, cashIn: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="overflow-hidden rounded-3xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 p-4 shadow-[0_48px_100px_-50px_rgb(0_0_0/0.85)] ring-1 ring-white/[0.04] backdrop-blur-sm md:p-6">
            <PanelTitle>Return on investment (ROI)</PanelTitle>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="rc-profit" tip="Total profit: sale proceeds + cumulative cash flow − initial investment (simplified).">
                  Total profit
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="rc-profit"
                    type="text"
                    inputMode="decimal"
                    value={v.profit}
                    onChange={(e) => setV((s) => ({ ...s, profit: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="rc-inv" tip="Same basis as profit denominator — total capital placed." required>
                  Total investment
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="rc-inv"
                    type="text"
                    inputMode="decimal"
                    value={v.invest}
                    onChange={(e) => setV((s) => ({ ...s, invest: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col overflow-hidden rounded-3xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 p-4 shadow-[0_48px_100px_-50px_rgb(0_0_0/0.85)] ring-1 ring-white/[0.04] backdrop-blur-sm md:p-6">
          <PanelTitle>Performance metrics</PanelTitle>
          {!hasAny ? (
            <ResultsEmpty message="Enter values to calculate ROI and cash-on-cash return." />
          ) : (
            <div className="flex min-h-[220px] flex-col justify-center gap-6">
              {coc !== null ? (
                <div>
                  <p className="font-body text-xs uppercase tracking-wide text-[var(--home-muted-on-dark)]">
                    Cash-on-cash return
                  </p>
                  <p className="font-display text-3xl font-bold text-emerald-300">{fmtPct(coc)}</p>
                </div>
              ) : null}
              {roi !== null ? (
                <div>
                  <p className="font-body text-xs uppercase tracking-wide text-[var(--home-muted-on-dark)]">ROI</p>
                  <p className="font-display text-3xl font-bold text-[var(--home-text-on-dark)]">{fmtPct(roi)}</p>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setV(DEFAULTS)}
        className="w-full rounded-2xl border border-white/15 bg-white/[0.04] py-3.5 font-body text-sm font-medium text-[var(--home-text-on-dark)] transition hover:bg-white/[0.08]"
      >
        Reset calculator
      </button>
    </div>
  );
}
